import styled from 'styled-components';

export const PageHeaderStyles = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  max-width: 1600px;
  margin: 0 auto;
  margin-bottom: ${({ theme }) => theme.spacing[6]};

  /* Leave breathing room so the fixed people panel does not overlap content on wide screens */
  @media (min-width: 1441px) {
    padding-right: calc(${({ theme }) => theme.spacing[6]} + 420px);
  }
`;

export const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

export const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.gray600};
  margin: 0;
`;

export const HealthBar = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[5]}`};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

export const HealthStats = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[6]};
  flex-wrap: wrap;
`;

export const HealthStat = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const HealthLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray600};
`;

export const HealthValue = styled.span<{ $variant?: 'default' | 'success' | 'warning' | 'danger' }>`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ $variant, theme }) => {
    switch ($variant) {
      case 'success': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'danger': return '#ef4444';
      default: return theme.colors.gray900;
    }
  }};
`;

export const HealthWarning = styled.div`
  margin-top: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  background: #fffbeb;
  border-left: 3px solid #f59e0b;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: #92400e;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

export const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
`;

export const TabDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray500};
  margin-top: ${({ theme }) => theme.spacing[1]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.regular};
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray700};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

export const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[6]}`};
  max-width: 1600px;
  margin: 0 auto;

  /* Leave breathing room so the fixed people panel does not overlap content on wide screens */
  @media (min-width: 1441px) {
    padding-right: calc(${({ theme }) => theme.spacing[6]} + 420px);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const DocumentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

export const DocumentTitle = styled.div`
  flex: 1;
`;

export const DocumentId = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray500};
  font-family: 'Monaco', 'Courier New', monospace;
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

export const DocumentName = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0;
`;

export const DocumentStatus = styled.div<{ $status: 'cleared' | 'pending' | 'blocking' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  background: ${({ $status }) => {
    switch ($status) {
      case 'cleared': return '#d1fae5';
      case 'pending': return '#fef3c7';
      case 'blocking': return '#fee2e2';
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case 'cleared': return '#065f46';
      case 'pending': return '#92400e';
      case 'blocking': return '#991b1b';
    }
  }};
`;

export const ImpactBadge = styled.div<{ $impact: 'non-blocking' | 'blocks-customs' | 'blocks-delivery' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ $impact }) => {
    switch ($impact) {
      case 'non-blocking': return '#059669';
      case 'blocks-customs': return '#dc2626';
      case 'blocks-delivery': return '#f59e0b';
    }
  }};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

export const DocumentMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray600};
`;

export const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const MetaLabel = styled.span`
  color: ${({ theme }) => theme.colors.gray500};
`;

export const MetaValue = styled.span`
  color: ${({ theme }) => theme.colors.gray900};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

export const DocumentActivity = styled.div`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  background: #f9fafb;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray600};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

export const ImpactSignal = styled.div`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  background: #fee2e2;
  border-left: 3px solid #dc2626;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: #7f1d1d;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  strong {
    color: #991b1b;
    display: block;
    margin-bottom: 4px;
  }

  span {
    display: inline-flex;
    gap: 12px;
  }
`;

export const DocumentActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  padding-top: ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
`;

export const PeopleCompact = styled.div`
  position: fixed;
  top: ${({ theme }) => theme.spacing[4]};
  right: ${({ theme }) => theme.spacing[4]};
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[3]};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  max-width: 250px;
`;

export const PeopleTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.gray900};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

export const PeopleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

export const PersonItem = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray600};
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[8]};
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

export const ChangeIndicator = styled.div<{ $type: 'updated-today' | 'no-change' | 'status-changed' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ $type }) => {
    switch ($type) {
      case 'updated-today': return '#0891b2';
      case 'status-changed': return '#f59e0b';
      case 'no-change': return '#6b7280';
    }
  }};
`;

export const PeopleWidget = styled.div`
  position: fixed;
  top: ${({ theme }) => theme.spacing[4]};
  right: ${({ theme }) => theme.spacing[6]};
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[4]};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  min-width: 220px;
  z-index: 100;
`;

export const PeopleWidgetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.gray900};
`;

export const PeopleBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

export const PeopleRole = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray600};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;

export const PeopleRoleBullet = styled.span`
  color: ${({ theme }) => theme.colors.gray400};\n`;

export const BlockingToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: #f9fafb;
  }

  /* Highlight when checkbox is checked */
  input[type="checkbox"]:checked ~ * {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const DocumentHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

export const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;
